/**
 * UIBridge — Facade connecting the new custom UI to the hidden native filter elements.
 *
 * The existing AbstractFilter system binds to native <select> and <input> elements
 * via CSS selectors. This bridge:
 * 1. Allows custom UI components to drive the hidden native elements
 * 2. Detects when the cascade system programmatically changes hidden elements
 * 3. Notifies observers (summary panel, etc.) of any filter changes
 */
export default class UIBridge {
  constructor() {
    this.filterMap = {};
    this.observers = [];
    this._pollIntervals = [];
  }

  /**
   * Register a filter mapping.
   * @param {string} filterName - Logical name (e.g., 'structure', 'manufacturer')
   * @param {Object} config
   * @param {string} config.nativeSelector - CSS selector for hidden native element
   * @param {string} config.type - 'select' or 'checkbox'
   * @param {Function} [config.onNativeChange] - Called when cascade changes native element
   */
  register(filterName, config) {
    this.filterMap[filterName] = config;

    const native = document.querySelector(config.nativeSelector);
    if (!native) {
      console.warn(`UIBridge: native element not found for ${filterName} (${config.nativeSelector})`);
      return;
    }

    // For select elements, poll for programmatic changes from the cascade system
    if (config.type === 'select' && config.onNativeChange) {
      let lastValue = native.value;
      let lastText = native.options[native.selectedIndex]?.text || '';

      const interval = setInterval(() => {
        const currentText = native.options[native.selectedIndex]?.text || '';
        if (currentText !== lastText) {
          lastText = currentText;
          lastValue = native.value;
          config.onNativeChange(currentText);
          this._notifyObservers(filterName, currentText);
        }
      }, 150);

      this._pollIntervals.push(interval);
    }

    // For checkboxes, poll for programmatic changes
    if (config.type === 'checkbox' && config.onNativeChange) {
      let lastChecked = native.checked;

      const interval = setInterval(() => {
        if (native.checked !== lastChecked) {
          lastChecked = native.checked;
          config.onNativeChange(native.checked);
          this._notifyObservers(filterName, native.checked);
        }
      }, 150);

      this._pollIntervals.push(interval);
    }
  }

  /**
   * Set a filter value. Finds the correct option in the hidden native element
   * and dispatches a change event so the existing cascade system runs.
   */
  setFilterValue(filterName, value) {
    const config = this.filterMap[filterName];
    if (!config) return;

    const native = document.querySelector(config.nativeSelector);
    if (!native) return;

    if (config.type === 'select') {
      // Find matching option by text or value
      for (let i = 0; i < native.options.length; i++) {
        if (native.options[i].text === value || native.options[i].value === value) {
          native.selectedIndex = i;
          break;
        }
      }
      native.dispatchEvent(new Event('change'));
    } else if (config.type === 'checkbox') {
      native.checked = !!value;
      native.dispatchEvent(new Event('change'));
    }

    this._notifyObservers(filterName, value);
  }

  /**
   * Get current value of a hidden native filter.
   */
  getFilterValue(filterName) {
    const config = this.filterMap[filterName];
    if (!config) return null;

    const native = document.querySelector(config.nativeSelector);
    if (!native) return null;

    if (config.type === 'select') {
      return native.options[native.selectedIndex]?.text || native.value;
    } else if (config.type === 'checkbox') {
      return native.checked;
    }
    return null;
  }

  /**
   * Register an observer for any filter change.
   */
  onChange(callback) {
    this.observers.push(callback);
  }

  _notifyObservers(filterName, value) {
    this.observers.forEach(cb => {
      try { cb(filterName, value); } catch (e) { console.error(e); }
    });
  }

  dispose() {
    this._pollIntervals.forEach(clearInterval);
    this._pollIntervals = [];
  }
}
